import {
  BBox,
  DEFAULT_LAYER_ID,
  constraintEntityIds,
  defaultLayers,
  entityBBox,
  makeLayer,
  type Constraint,
  type Entity,
  type EntityId,
  type Layer,
  type LayerInit,
} from '@lakaran/geometry';
import { Emitter } from './emitter';
import { SpatialIndex } from './spatial';

export interface DocumentChange {
  added: readonly Entity[];
  removed: readonly Entity[];
  updated: readonly Entity[];
}

export interface DocumentEvents extends Record<string, unknown> {
  change: DocumentChange;
  layers: void;
}

/**
 * The drawing database: layers plus entities plus the broad-phase index.
 * Everything here is renderer agnostic and serialisable in principle.
 */
export class CadDocument {
  readonly layers = new Map<string, Layer>();
  readonly entities = new Map<EntityId, Entity>();
  /** Modelling constraints (point, edge and snap derived), keyed by id. */
  readonly constraints = new Map<string, Constraint>();
  readonly events = new Emitter<DocumentEvents>();
  currentLayerId: string = DEFAULT_LAYER_ID;

  private readonly index = new SpatialIndex();
  private readonly order = new Map<EntityId, number>();
  private orderSeq = 0;
  private orderDirty = false;
  private orderedCache: EntityId[] = [];

  constructor() {
    for (const layer of defaultLayers()) this.layers.set(layer.id, layer);
  }

  get entityCount(): number {
    return this.entities.size;
  }

  getLayer(id: string): Layer | undefined {
    return this.layers.get(id);
  }

  ensureLayer(id: string, init: LayerInit = {}): Layer {
    const existing = this.layers.get(id);
    if (existing) return existing;
    const layer = makeLayer(id, init);
    this.layers.set(id, layer);
    this.events.emit('layers', undefined);
    return layer;
  }

  addLayer(layer: Layer): void {
    this.layers.set(layer.id, layer);
    this.events.emit('layers', undefined);
  }

  updateLayer(id: string, patch: Partial<Layer>): void {
    const layer = this.layers.get(id);
    if (!layer) return;
    this.layers.set(id, { ...layer, ...patch, id });
    this.events.emit('layers', undefined);
    this.events.emit('change', { added: [], removed: [], updated: [] });
  }

  /** Removes an empty layer; returns false when it still owns entities. */
  removeLayer(id: string): boolean {
    if (id === DEFAULT_LAYER_ID) return false;
    for (const entity of this.entities.values()) {
      if (entity.layerId === id) return false;
    }
    const removed = this.layers.delete(id);
    if (removed) this.events.emit('layers', undefined);
    return removed;
  }

  setCurrentLayer(id: string): void {
    this.ensureLayer(id);
    this.currentLayerId = id;
    this.events.emit('layers', undefined);
  }

  add(entity: Entity): void {
    this.entities.set(entity.id, entity);
    this.index.insert(entity.id, entityBBox(entity));
    this.order.set(entity.id, ++this.orderSeq);
    this.orderDirty = true;
    this.events.emit('change', { added: [entity], removed: [], updated: [] });
  }

  addMany(entities: readonly Entity[]): void {
    if (entities.length === 0) return;
    for (const entity of entities) {
      this.entities.set(entity.id, entity);
      this.index.insert(entity.id, entityBBox(entity));
      this.order.set(entity.id, ++this.orderSeq);
    }
    this.orderDirty = true;
    this.events.emit('change', { added: entities, removed: [], updated: [] });
  }

  remove(id: EntityId): Entity | undefined {
    const entity = this.entities.get(id);
    if (!entity) return undefined;
    this.entities.delete(id);
    this.index.remove(id);
    this.order.delete(id);
    this.orderDirty = true;
    this.pruneConstraints();
    this.events.emit('change', { added: [], removed: [entity], updated: [] });
    return entity;
  }

  removeMany(ids: Iterable<EntityId>): Entity[] {
    const removed: Entity[] = [];
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (!entity) continue;
      this.entities.delete(id);
      this.index.remove(id);
      this.order.delete(id);
      removed.push(entity);
    }
    if (removed.length > 0) {
      this.orderDirty = true;
      this.pruneConstraints();
      this.events.emit('change', { added: [], removed, updated: [] });
    }
    return removed;
  }

  /** Replaces an entity in place (same id). */
  update(entity: Entity): void {
    const previous = this.entities.get(entity.id);
    if (!previous) {
      this.add(entity);
      return;
    }
    this.entities.set(entity.id, entity);
    this.index.update(entity.id, entityBBox(entity));
    this.events.emit('change', { added: [], removed: [], updated: [entity] });
  }

  // ------------------------------------------------------------ constraints

  get constraintCount(): number {
    return this.constraints.size;
  }

  addConstraint(constraint: Constraint): void {
    this.constraints.set(constraint.id, constraint);
    this.events.emit('change', { added: [], removed: [], updated: [] });
  }

  removeConstraint(id: string): Constraint | undefined {
    const constraint = this.constraints.get(id);
    if (!constraint) return undefined;
    this.constraints.delete(id);
    this.events.emit('change', { added: [], removed: [], updated: [] });
    return constraint;
  }

  /** Constraints that reference an entity, in insertion order. */
  constraintsOf(entityId: EntityId): Constraint[] {
    const out: Constraint[] = [];
    for (const constraint of this.constraints.values()) {
      if (constraintEntityIds(constraint).includes(entityId)) out.push(constraint);
    }
    return out;
  }

  clearConstraints(): void {
    if (this.constraints.size === 0) return;
    this.constraints.clear();
    this.events.emit('change', { added: [], removed: [], updated: [] });
  }

  /** Drops constraints whose geometry no longer exists. */
  pruneConstraints(): number {
    let dropped = 0;
    for (const [id, constraint] of [...this.constraints]) {
      if (constraintEntityIds(constraint).every((entityId) => this.entities.has(entityId))) continue;
      this.constraints.delete(id);
      dropped += 1;
    }
    return dropped;
  }

  get(id: EntityId): Entity | undefined {
    return this.entities.get(id);
  }

  has(id: EntityId): boolean {
    return this.entities.has(id);
  }

  /** Entities whose bounding box overlaps `box`, in draw order. */
  query(box: BBox): Entity[] {
    const ids = this.index.query(box);
    const result: Entity[] = [];
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (entity) result.push(entity);
    }
    return this.sortByDrawOrder(result);
  }

  /** Entities whose bounding box overlaps `box` and whose layer is visible. */
  queryVisible(box: BBox): Entity[] {
    return this.query(box).filter((entity) => this.isVisible(entity));
  }

  all(): Entity[] {
    if (this.orderDirty) {
      this.orderedCache = [...this.entities.keys()].sort(
        (a, b) => (this.order.get(a) ?? 0) - (this.order.get(b) ?? 0),
      );
      this.orderDirty = false;
    }
    const out: Entity[] = [];
    for (const id of this.orderedCache) {
      const entity = this.entities.get(id);
      if (entity) out.push(entity);
    }
    return out;
  }

  isVisible(entity: Entity): boolean {
    const layer = this.layers.get(entity.layerId);
    return layer ? layer.visible : true;
  }

  drawOrderOf(id: EntityId): number {
    return this.order.get(id) ?? 0;
  }

  sortByDrawOrder(entities: Entity[]): Entity[] {
    return entities.sort((a, b) => this.drawOrderOf(a.id) - this.drawOrderOf(b.id));
  }

  bounds(visibleOnly = false): BBox {
    let box = BBox.empty();
    for (const entity of this.entities.values()) {
      if (visibleOnly && !this.isVisible(entity)) continue;
      box = box.union(entityBBox(entity));
    }
    return box;
  }

  clear(): void {
    const removed = [...this.entities.values()];
    this.entities.clear();
    this.index.clear();
    this.order.clear();
    this.orderDirty = true;
    this.constraints.clear();
    if (removed.length > 0) {
      this.events.emit('change', { added: [], removed, updated: [] });
    }
  }

  /** Rebuilds the broad-phase grid for the current drawing extent. */
  reindex(): void {
    const bounds = this.bounds();
    if (bounds.isEmpty) return;
    this.index.repartition(SpatialIndex.suggestCellSize(bounds));
  }
}
